import { SAY_KINDS } from "@typedoc/interview/session";
import { z } from "zod";
import { idSchema } from "./ids";
import { recordingStateSchema } from "./recordings";

/**
 * The vocabulary of the voice worker's channel to the API (ENG-1017): what Abacor should say, and
 * the grant that lets the worker report what it heard for one recording without holding a sign-in
 * token. The worker holds no interview state: it is told the one line to say and sends back the
 * events in `voiceEventSchema` (`@typedoc/interview/events`).
 *
 * This module is browser-safe: it imports nothing from Node or a server.
 */

/**
 * How long a voice grant works. One grant serves the whole recording, and a recording stops at an
 * hour, so this is that hour with time to spare. The API issues it when it dispatches the worker.
 */
export const VOICE_GRANT_SECONDS = 2 * 60 * 60;

/** How long after a recording ends its voice grant still reports, so the last outcome lands. */
export const VOICE_GRACE_SECONDS = 120;

/** The line Abacor should say now. The worker drops it unexpired-or-not by its own `expiresAt`. */
export const voiceSayIntentSchema = z.strictObject({
  /** The tape position that made it: `say.outcome` names it. */
  intent: z.number().int().positive(),
  kind: z.enum(SAY_KINDS),
  text: z.string().min(1).max(4_000),
  /** Said first, before a cut-in. */
  cue: z.string().min(1).max(4_000).nullable(),
  /** The worker already started saying it; a worker that finds it started does not say it again. */
  started: z.boolean(),
  /** Milliseconds left to start saying it, counted from the moment the frame was made. */
  ttlMs: z.number().int().nonnegative(),
});
export type VoiceSayIntent = z.infer<typeof voiceSayIntentSchema>;

/**
 * One frame of the worker's stream: what the recording is and the one thing to say. A frame goes out
 * when either changes; the first always goes. The worker keeps the latest and speaks `say` once.
 */
export const voiceFrameSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal("voice"),
  state: recordingStateSchema,
  /** Abacor's voice is muted: its lines are written but not spoken. */
  muted: z.boolean(),
  say: voiceSayIntentSchema.nullable(),
  /** The tape position this frame was computed from. */
  lastSeq: z.number().int().nonnegative(),
});
export type VoiceFrame = z.infer<typeof voiceFrameSchema>;

/** What the API did with one event: where it landed, and why the session set it aside, if it did. */
export const voiceRecordedSchema = z.strictObject({
  seq: z.number().int().positive(),
  duplicate: z.boolean(),
  dropped: z.string().nullable(),
});
export type VoiceRecorded = z.infer<typeof voiceRecordedSchema>;

/**
 * The participant attribute a person's island sets to say whether it is listening: "false" while
 * it is in the room before the recording starts (choosing what to share), "true" from Start. A
 * person without it is listening once they take the voice, as islands always were.
 */
export const VOICE_LISTENING_ATTRIBUTE = "listening";

/** The name the voice worker registers under with LiveKit, so the API can dispatch it to a room. */
export const VOICE_AGENT_NAME = "abacor-voice";

/**
 * What the API hands the voice worker when it dispatches it to a recording's room: where the
 * regional API is, which recording, and the voice grant that opens only that recording's channel.
 * LiveKit carries it as the dispatch's metadata.
 */
export const voiceJobSchema = z.strictObject({
  apiBase: z.url(),
  workspaceId: z.string().min(1),
  recordingId: idSchema("recording"),
  token: z.string().min(1).max(200),
  /**
   * Names the person is likely to say (suppliers, systems, codes), to bias the speech-to-text.
   * AssemblyAI takes up to 100 of up to 50 characters; the worker keeps those that fit.
   */
  keyterms: z.array(z.string().min(1).max(200)).max(500).optional(),
});
export type VoiceJob = z.infer<typeof voiceJobSchema>;

/**
 * What the island needs to join a recording's voice room: LiveKit's address, a token for this
 * person in this recording's room only, and when it stops working (epoch milliseconds). The room
 * is the recording's ID. Asking again returns a fresh token, and makes sure the worker is there.
 */
export const voiceJoinSchema = z.strictObject({
  url: z.string().min(1).max(300),
  room: idSchema("recording"),
  token: z.string().min(1).max(4_000),
  expiresAt: z.number().int().positive(),
});
export type VoiceJoin = z.infer<typeof voiceJoinSchema>;

/**
 * How an island asks for its way in: `listen=later` when it joins before it listens (while the
 * person chooses what to share), so its token marks it not listening from the moment it is in the
 * room. Without it, the token says nothing, as for an older island.
 */
export const voiceJoinQuerySchema = z.object({ listen: z.enum(["later"]).optional() });

/** The speech a worker reports: speech-to-text (minutes billed) and text-to-speech (characters billed). */
export const SPEECH_KINDS = ["stt", "tts"] as const;
export type SpeechKind = (typeof SPEECH_KINDS)[number];

/** The largest Postgres `integer`, the type of the usage table's counts. */
const MAX_COUNT = 2_147_483_647;

/**
 * What one job has used of one speech model since it started, in the units the vendors bill.
 * Totals, not increments: a report sent twice, late or out of order says nothing new.
 */
export const speechUsageSchema = z.strictObject({
  kind: z.enum(SPEECH_KINDS),
  /** As agents-js names them: through LiveKit Inference the provider is `livekit`. */
  provider: z.string().min(1).max(100),
  model: z.string().min(1).max(200),
  /** Speech-to-text: audio streamed to the recognizer, silence included. Text-to-speech: audio made. */
  audioMs: z.number().int().nonnegative().max(MAX_COUNT),
  /** Text-to-speech: characters rendered, said or not. Zero for speech-to-text. */
  characters: z.number().int().nonnegative().max(MAX_COUNT),
});
export type SpeechUsage = z.infer<typeof speechUsageSchema>;

/**
 * A worker's report of its speech usage for one recording (decision `2026-10-30-voice-worker-speech-usage-ledger`): the running totals of
 * one LiveKit job, one entry per model. Sent every minute while the job runs and once when it
 * ends, so a job that dies loses at most a minute of it.
 */
export const voiceUsageSchema = z.strictObject({
  /** LiveKit's job ID: a replacement worker is a new job with totals of its own. */
  job: z.string().min(1).max(200),
  /** At most one entry per kind, provider and model. */
  models: z
    .array(speechUsageSchema)
    .max(20)
    .refine(
      (models) =>
        new Set(models.map((m) => `${m.kind}\n${m.provider}\n${m.model}`)).size === models.length,
      "A model is reported once.",
    ),
});
export type VoiceUsage = z.infer<typeof voiceUsageSchema>;
